import axios from 'axios';
import * as KEYS from '../constants/keys.js';
import * as ACTIONS from '../constants/actions.js';
axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL || '/api';

export default class API {
    constructor({dispatch}){
        this.dispatch = dispatch;
        this.token = this.getAuthToken();
    }

    setAuthToken = (token) => {
        if (!token) return;
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        localStorage.setItem(KEYS.USER_TOKEN_KEY, token);
        this.token = token;
    }

    getAuthToken = () => {
        const token = localStorage.getItem(KEYS.USER_TOKEN_KEY);
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            return token;
        }
        return null;
    }

    clearAuthToken = () => {
        delete axios.defaults.headers.common['Authorization'];
        localStorage.removeItem(KEYS.USER_TOKEN_KEY);
        this.token = null;
        this.dispatch({ type: ACTIONS.CLEAR_USER });
        this.dispatch({ type: ACTIONS.CLEAR_ACCOUNTS });
    }

    signIn = async (credentials) => {
        try {
            const response = await axios.post('/agents/sign-in', credentials);
            this.setAuthToken(response.data.token);
            this.dispatch({ type: ACTIONS.SET_USER, payload: response.data.agent });
            return response.data;
        } catch (error) {
            console.error('Error signing in:', error);
            throw error;
        }
    }

    checkAuth = async () => {
        try {
            const { data } = await axios.get('/agents/check-auth');
            console.log(data);
            this.dispatch({ type: ACTIONS.SET_USER, payload: data.agent });
            return true;
        } catch (error) {
            console.error('Error checking auth:', error);
            this.clearAuthToken();
            return false;
        }
    }

    searchAccounts = async (searchParams) => {
        try {
            const response = await axios.post('/accounts/search', searchParams);
            this.dispatch({ type: ACTIONS.SET_ACCOUNTS, payload: response.data.results || [] });
            return response.data.results || [];
        } catch (error) {
            console.error('Error searching accounts:', error);
            return null;
        }
    }

    getAccountInformationById = async (id) => {
        try {
            const response = await axios.post('/accounts/get-account-information-by-id', { id });
            return response.data.result || null;
        } catch (error) {
            console.error('Error getting account information by ID:', error);
            return null;
        }
    }

    grantEntitlementToAccount = async (accountId, metadata) => {
        try {
            const response = await axios.post('/accounts/grant-entitlement-to-account', { accountId, metadata });
            return true;
        } catch (error) {
            console.error('Error granting entitlement to account:', error);
            return false;
        }
    }

    changeUserEmail = async (accountId, newEmail) => {
        try {
            const response = await axios.post('/accounts/update-user-email', { accountId, newEmail });
            return true;
        } catch(error){
            console.error('Error changing user email:', error);
            return false;
        }
    }

    resendVerificationEmail = async (accountId) => {
        try {
            const response = await axios.post('/accounts/resend-user-verification-email', { accountId });
            return true;
        } catch(err){
            console.error('Error resending verification email:', err);
            return false;
        }
    }

    triggerForgotPassword = async (email) => {
        try {
            const response = await axios.post('/accounts/trigger-forgot-password-email', { email });
            return true;
        } catch(err){
            console.error('Error triggering forgot password email:', err);
            return false;
        }
    }

    changeUserPassword = async (accountId, newPassword) => {
        try {
            const response = await axios.post('/accounts/change-user-password', { accountId, newPassword });
            return true;
        } catch(err){
            console.error('Error changing user password:', err);
            return false;
        }
    }

    verifyAccount = async (accountId) => {
        try {
            const response = await axios.post('/accounts/verify-account', { accountId });
            return true;
        } catch(err){
            console.error('Error verifying account:', err);
            return false;
        }
    }

    //Admin APIs

    getAllAgents = async () => {
        try {
            const response = await axios.get('/admin/agents');
            return response.data || [];
        } catch (error) {
            console.error('Error fetching agents:', error);
            return [];
        }
    }

    createAgent = async (agentData) => {
        try {
            const response = await axios.post('/admin/create-agent', agentData);
            return response.data;
        } catch (error) {
            console.error('Error creating agent:', error);
            throw error;
        }
    }

    updateAgent = async (agentData) => {
        try {
            const response = await axios.post('/admin/update-agent', agentData);
            return response.data;
        } catch (error) {
            console.error('Error updating agent:', error);
            throw error;
        }
    }

    deleteAgent = async (agentId) => {
        try {
            const response = await axios.post('/admin/delete-agent', { id: agentId });
            return response.data;
        } catch (error) {
            console.error('Error deleting agent:', error);
            throw error;
        }
    }

    updateAgentPassword = async (agentId, newPassword) => {
        try {
            const response = await axios.post('/admin/update-agent-password', { id: agentId, new_password: newPassword });
            return response.data;
        } catch (error) {
            console.error('Error updating agent password:', error);
            throw error;
        }
    }

    lookupIAPOrder = async (orderId) => {
        try {
            const response = await axios.post('/accounts/lookup-iap-order', { orderId });
            return response.data;
        } catch (error) {
            console.error('Error looking up IAP order:', error);
            throw error;
        }
    }

}